import { ClickAwayListener } from "@mui/base/ClickAwayListener";
import { Popper } from "@mui/base/Popper";
import clsx from "clsx";
import React, { useEffect, useState } from "react";
import { NavLink, matchPath } from "react-router-dom";

import { Icon } from "~/assets/icons/Icon";

import style from "./DashboardSidebar.module.scss";

export type MenuItem = {
  id?: string;
  label: string;
  iconName: React.ComponentProps<typeof Icon>["iconName"];
  link?: string;
  feature?: string;
  subMenu?: {
    label: string;
    link: string;
    feature?: string;
    openInNewTab?: boolean;
  }[];
  isOpen?: boolean;
  tag?: string;
};

export type MenuGroup = {
  menuTitle: string;
  menuItems: MenuItem[];
};

type MenuProps = {
  items?: MenuItem[];
  groups?: MenuGroup[];
  isSidebarOpen: boolean;
  activePath: string;
};

const isMenuTabActive = (
  currentPath: undefined | string,
  itemPath: undefined | string,
) => {
  if (currentPath && itemPath) {
    const isSettings = !!matchPath({ path: "/sites/:id/settings" }, itemPath);

    return isSettings
      ? !!matchPath({ path: "/sites/:id/settings/*", end: false }, currentPath)
      : currentPath === itemPath;
  } else {
    return false;
  }
};

const Menu: React.FC<MenuProps> = ({
  items,
  groups,
  isSidebarOpen,
  activePath,
}) => {
  const [openSubmenus, setOpenSubmenus] = useState<{ [key: string]: boolean }>(
    {},
  );
  const [popoverAnchorEl, setPopoverAnchorEl] = useState<HTMLElement | null>(
    null,
  );
  const [popoverItems, setPopoverItems] = useState<MenuItem["subMenu"] | null>(
    null,
  );

  useEffect(() => {
    if (!isSidebarOpen) {
      setOpenSubmenus({});
    }
  }, [isSidebarOpen]);

  const handleToggleSubMenu = (label: string) => {
    setOpenSubmenus((prev) => ({ ...prev, [label]: !prev[label] }));
  };

  const handleOpenPopover = (
    event: React.MouseEvent<HTMLElement>,
    subMenu: NonNullable<MenuItem["subMenu"]>,
  ) => {
    setPopoverAnchorEl(event.currentTarget);
    setPopoverItems(subMenu);
  };

  const handleClosePopover = () => {
    setPopoverAnchorEl(null);
    setPopoverItems(null);
  };

  const renderMenuItem = (item: MenuItem) => {
    if (item.subMenu) {
      return (
        <div key={item.label} className={style.menuSeparator}>
          <div
            className={clsx(style.menuLink, {
              [style.isOpen]: openSubmenus[item.label] || item.isOpen,
            })}
            onClick={(event) => {
              if (isSidebarOpen) {
                handleToggleSubMenu(item.label);
              } else {
                item.subMenu && handleOpenPopover(event, item.subMenu);
              }
            }}
          >
            <span className={style.menuLinkIconTitle}>
              <span className={style.menuIconTitle}>
                <Icon iconName={item.iconName} width="1.5rem" height="1.5rem" />
                <span className={clsx(style.summenuIconTitle)}>
                  <span
                    className={clsx(style.label, {
                      [style.hiddenLabel]: !isSidebarOpen,
                    })}
                  >
                    {item.label}
                  </span>
                  {item.tag && (
                    <span
                      // @ts-expect-error TS2551 non-existing className
                      className={clsx(style["sidebar__tag"], {
                        [style.hiddenLabel]: !isSidebarOpen,
                      })}
                    >
                      {item.tag}
                    </span>
                  )}
                  <span className={style.rightIcon}>
                    {openSubmenus[item.label] || item.isOpen ? (
                      <Icon iconName="IconUp" width="1rem" height="1.5rem" />
                    ) : (
                      <Icon iconName="IconDown" width="1rem" height="1.5rem" />
                    )}
                  </span>
                </span>
              </span>
            </span>
          </div>
          {isSidebarOpen && (openSubmenus[item.label] || item.isOpen) && (
            <div className={style.subMenu}>
              {item.subMenu.map((subItem) => (
                <NavLink
                  key={subItem.label}
                  to={subItem.link}
                  target={subItem.openInNewTab ? "_blank" : undefined}
                  rel={subItem.openInNewTab ? "noopener noreferrer" : undefined}
                  className={clsx(style.subMenuLink, {
                    [style.menuLinkActive]: isMenuTabActive(
                      activePath,
                      subItem.link,
                    ),
                  })}
                >
                  <span
                    className={clsx(style.label, {
                      [style.hiddenLabel]: !isSidebarOpen,
                    })}
                  >
                    {subItem.label}
                  </span>
                </NavLink>
              ))}
            </div>
          )}
        </div>
      );
    }
    if (item.link) {
      return (
        <div key={item.label} className={style.menuSeparator}>
          <NavLink
            to={item.link}
            className={clsx(style.menuLink, {
              // @ts-expect-error TS2339 non-existing className
              [style.menuLinkClosed]: !isSidebarOpen,
              [style.menuLinkActive]: isMenuTabActive(activePath, item.link),
            })}
          >
            <span className={style.menuLinkIconTitle}>
              <span className={style.menuIconTitle}>
                <Icon
                  iconName={item.iconName}
                  width="1.5rem"
                  height="1.5rem"
                  className={clsx({
                    [style.fillHover]: item.iconName === "InterlinksIcon",
                    [style.pagesIcon]: item.iconName === "PagesIcon",
                  })}
                />
                <span
                  className={clsx(style.label, {
                    [style.hiddenLabel]: !isSidebarOpen,
                  })}
                >
                  {item.label}
                </span>
                {item.tag && (
                  <span
                    // @ts-expect-error TS2551 non-existing className
                    className={clsx(style["sidebar__tag"], {
                      [style.hiddenLabel]: !isSidebarOpen,
                    })}
                  >
                    {item.tag}
                  </span>
                )}
              </span>
            </span>
          </NavLink>
        </div>
      );
    }

    return null;
  };

  return (
    <>
      {groups && groups.length > 0 ? (
        <div>
          {groups.map((group) => (
            <div key={group.menuTitle}>
              {isSidebarOpen ? (
                <div className={style.sidebarMenuTitle}>{group.menuTitle}</div>
              ) : (
                <div className={style.sidebarMenuTitleClosed}></div>
              )}
              {group.menuItems.map((item) => renderMenuItem(item))}
            </div>
          ))}
        </div>
      ) : items ? (
        <div>{items.map((item) => renderMenuItem(item))}</div>
      ) : null}
      {popoverAnchorEl && popoverItems && (
        <Popper
          open={Boolean(popoverAnchorEl)}
          anchorEl={popoverAnchorEl}
          placement="right"
          style={{
            zIndex: 300,
          }}
        >
          <ClickAwayListener onClickAway={handleClosePopover}>
            <div className={style.popperWrapper}>
              {popoverItems.map((subItem) => (
                <NavLink
                  key={subItem.label}
                  to={subItem.link}
                  target={subItem.openInNewTab ? "_blank" : undefined}
                  rel={subItem.openInNewTab ? "noopener noreferrer" : undefined}
                  // @ts-expect-error TS2339 non-existing className
                  className={clsx(style.popoverLink, {
                    [style.menuLinkActive]: isMenuTabActive(
                      activePath,
                      subItem.link,
                    ),
                    [style.popoverItem]: !isSidebarOpen,
                  })}
                  onClick={handleClosePopover}
                >
                  <span>{subItem.label}</span>
                </NavLink>
              ))}
            </div>
          </ClickAwayListener>
        </Popper>
      )}
    </>
  );
};

export default Menu;
